/**
 * PartnerTierUpgradePage — Phase K.3.3(b) partner-facing upgrade UI.
 *
 * Route: /partner/tier-upgrade (gated on partner_is_fully_live).
 *
 * Copy discipline: partner-facing surface — dollar-outcome voice
 * only, ZERO explicit percent literals in visible text, no
 * "vetted"/"screened"/etc. The admin-side ops-percent whitelist
 * marker is FORBIDDEN on this file — this is a partner-facing
 * surface, not an internal ops tile.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  Crown,
  ShieldCheck,
  ArrowUpRight,
  X,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import FloatingThemeToggle from "@/components/FloatingThemeToggle";

const _STATUS_ENDPOINT = "/partner/tier-upgrade/status";
const _REQUEST_ENDPOINT = "/partner/tier-upgrade/request";
const _WITHDRAW_ENDPOINT = "/partner/tier-upgrade/withdraw";
const _TIERS_ENDPOINT = "/partner/tiers/public";

function _dollars(cents) {
  const d = Math.round(cents) / 100;
  return d.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

// Dollar-outcome benefit copy — brand-cheerful, factual.
// No percent literals, no rate-derivable framing.
const _TIER_BENEFITS = {
  standard: [
    "Get started at zero cost.",
    "Show up in customer search when they book in your ZIPs.",
    "Keep every booking you accept — no monthly fee.",
  ],
  preferred: [
    "Keep more of every booking you complete.",
    "Cover up to 10 ZIP codes across your metro.",
    "Priority routing when customers request same-day pickups.",
  ],
  elite: [
    "Keep the most per booking of any tier.",
    "Cover up to 25 ZIP codes — perfect for growing crews.",
    "Featured placement in the customer-facing search grid.",
    "Direct line to a partner-success manager.",
  ],
};

const _TIER_ZIP_CAPS = {
  standard: 5,
  preferred: 10,
  elite: 25,
};

/* ------------------------------------------------------------------ *
 * Tier card
 * ------------------------------------------------------------------ */
function TierCard({
  tier,
  currentActiveId,
  pendingTargetId,
  onRequestClick,
  disabled,
}) {
  const isCurrent = tier.id === currentActiveId;
  const currentPrice = _TIER_ZIP_CAPS[currentActiveId] ?? 0;
  const targetPrice = _TIER_ZIP_CAPS[tier.id] ?? 0;
  const isBelow = targetPrice < currentPrice;
  const isAbove = targetPrice > currentPrice;
  const pendingForThis = pendingTargetId === tier.id;
  const pendingForOther =
    pendingTargetId && pendingTargetId !== tier.id;

  let ctaVariant = "outline";
  let ctaLabel = "";
  let ctaTestid = "";
  let ctaDisabled = disabled;

  if (isCurrent) {
    ctaLabel = "Your current tier";
    ctaDisabled = true;
    ctaTestid = `tier-cta-current-${tier.id}`;
  } else if (isBelow) {
    ctaLabel = "Downgrade — contact support";
    ctaDisabled = true;
    ctaTestid = `tier-cta-downgrade-${tier.id}`;
  } else if (isAbove && pendingForThis) {
    ctaLabel = "Upgrade requested — admin notified";
    ctaDisabled = true;
    ctaTestid = `tier-cta-pending-${tier.id}`;
  } else if (isAbove && pendingForOther) {
    ctaLabel = "Request upgrade instead";
    ctaVariant = "default";
    ctaTestid = `tier-cta-replace-${tier.id}`;
  } else if (isAbove) {
    ctaLabel = "Request upgrade";
    ctaVariant = "default";
    ctaTestid = `tier-cta-request-${tier.id}`;
  }

  return (
    <div
      data-testid={`tier-card-${tier.id}`}
      data-is-current={isCurrent ? "true" : "false"}
      style={{
        border: `2px solid ${isCurrent ? "#F89623" : "#e5e7eb"}`,
        borderRadius: 14,
        padding: 20,
        background: "#ffffff",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        minHeight: 320,
      }}
    >
      {isCurrent && (
        <span
          data-testid={`tier-current-chip-${tier.id}`}
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            background: "#fff7ed",
            color: "#c2410c",
            padding: "3px 10px",
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.6,
            border: "1px solid #fed7aa",
          }}
        >
          Current tier
        </span>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 4,
        }}
      >
        {tier.id === "elite" && <Crown size={18} color="#F89623" />}
        {tier.id === "preferred" && (
          <ShieldCheck size={18} color="#F89623" />
        )}
        <h3
          data-testid={`tier-name-${tier.id}`}
          style={{
            fontSize: 20,
            margin: 0,
            fontWeight: 800,
            color: "#374751",
          }}
        >
          {tier.name}
        </h3>
      </div>
      <div
        data-testid={`tier-price-${tier.id}`}
        style={{
          fontSize: 24,
          fontWeight: 800,
          color: "#374751",
          marginBottom: 12,
        }}
      >
        ${_dollars(tier.subscription_price_cents)}
        <span
          style={{ fontSize: 13, color: "#6b7280", fontWeight: 500 }}
        >
          {" "}/month
        </span>
      </div>

      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: "0 0 20px 0",
          fontSize: 13,
          color: "#374751",
          lineHeight: 1.5,
        }}
      >
        {(_TIER_BENEFITS[tier.id] || []).map((line, i) => (
          <li
            key={i}
            style={{ display: "flex", gap: 6, marginBottom: 6 }}
          >
            <CheckCircle2 size={14} color="#F89623" style={{ marginTop: 3, flexShrink: 0 }} />
            <span>{line}</span>
          </li>
        ))}
      </ul>

      <div
        data-testid={`tier-zips-${tier.id}`}
        style={{
          fontSize: 11,
          color: "#6b7280",
          marginBottom: 14,
          padding: "6px 10px",
          background: "#f9fafb",
          borderRadius: 6,
          textAlign: "center",
        }}
      >
        Up to <b>{_TIER_ZIP_CAPS[tier.id] ?? "?"}</b> ZIP codes
      </div>

      <div style={{ marginTop: "auto" }}>
        <Button
          data-testid={ctaTestid}
          onClick={() =>
            !ctaDisabled &&
            onRequestClick &&
            onRequestClick({ target: tier, replacing: pendingForOther })
          }
          disabled={ctaDisabled}
          variant={ctaVariant}
          style={{
            width: "100%",
            background: ctaVariant === "default" && !ctaDisabled ? "#F89623" : undefined,
            color: ctaVariant === "default" && !ctaDisabled ? "#ffffff" : undefined,
          }}
        >
          {ctaLabel}
          {ctaVariant === "default" && !ctaDisabled && (
            <ArrowUpRight size={14} style={{ marginLeft: 6 }} />
          )}
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Confirmation modal
 * ------------------------------------------------------------------ */
function ConfirmModal({ open, target, replacing, onCancel, onConfirm, sending }) {
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) setNote("");
  }, [open]);

  if (!open || !target) return null;

  return (
    <div
      data-testid="tier-upgrade-confirm-overlay"
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(17, 24, 39, 0.55)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "48px 16px",
        zIndex: 1000,
        overflowY: "auto",
      }}
    >
      <div
        data-testid="tier-upgrade-confirm-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#ffffff",
          borderRadius: 14,
          maxWidth: 520,
          width: "100%",
          padding: 24,
          boxShadow: "0 25px 45px -12px rgba(0,0,0,0.25)",
        }}
      >
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 12,
            gap: 12,
          }}
        >
          <h2
            data-testid="tier-upgrade-confirm-title"
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 800,
              color: "#374751",
            }}
          >
            Request upgrade to {target.name}
          </h2>
          <button
            data-testid="tier-upgrade-confirm-close"
            onClick={onCancel}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: 4,
            }}
            aria-label="Close"
          >
            <X size={20} color="#6b7280" />
          </button>
        </header>

        <p
          data-testid="tier-upgrade-confirm-copy"
          style={{ fontSize: 14, color: "#374751", lineHeight: 1.5 }}
        >
          We'll let our team know you're interested in the{" "}
          <strong>{target.name}</strong> tier.{" "}
          <strong>Real subscription billing is coming soon</strong>{" "}
          — until then, your admin will manually activate your new tier
          once we're ready. You'll get an email when it happens.
        </p>

        {replacing && (
          <div
            data-testid="tier-upgrade-replacing-notice"
            style={{
              padding: "8px 12px",
              background: "#fff7ed",
              border: "1px solid #fed7aa",
              borderRadius: 8,
              color: "#9a3412",
              fontSize: 12,
              marginTop: 12,
              display: "flex",
              gap: 6,
              alignItems: "center",
            }}
          >
            <AlertCircle size={14} />
            This replaces your pending upgrade request.
          </div>
        )}

        <label
          style={{
            display: "block",
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.6,
            color: "#6b7280",
            marginTop: 16,
            marginBottom: 4,
          }}
        >
          Anything you want to tell us? (optional)
        </label>
        <textarea
          data-testid="tier-upgrade-confirm-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={4}
          placeholder="Any timing or coverage notes for the team…"
          maxLength={1000}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "8px 10px",
            border: "1px solid #e5e7eb",
            borderRadius: 6,
            fontSize: 13,
            color: "#374751",
            outline: "none",
            resize: "vertical",
            minHeight: 80,
          }}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 18,
          }}
        >
          <Button
            variant="ghost"
            data-testid="tier-upgrade-confirm-cancel"
            onClick={onCancel}
            disabled={sending}
          >
            Cancel
          </Button>
          <Button
            data-testid="tier-upgrade-confirm-submit"
            onClick={() => onConfirm(note)}
            disabled={sending}
            style={{ background: "#F89623", color: "#ffffff" }}
          >
            {sending ? (
              <>
                <Loader2 size={14} className="animate-spin" style={{ marginRight: 6 }} />
                Sending…
              </>
            ) : (
              <>Confirm upgrade request</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Pending intent card
 * ------------------------------------------------------------------ */
function PendingIntentCard({ status, onWithdraw, withdrawing }) {
  if (!status?.pending_intent_row) return null;
  const target = status.selected_tier_intent;
  return (
    <section
      data-testid="tier-upgrade-pending-card"
      style={{
        border: "1px solid #fed7aa",
        background: "#fff7ed",
        borderRadius: 12,
        padding: 20,
        marginTop: 20,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <div>
        <div
          data-testid="tier-upgrade-pending-heading"
          style={{ fontSize: 14, fontWeight: 800, color: "#9a3412" }}
        >
          You've requested {target?.name || "an upgrade"}.
        </div>
        <div style={{ fontSize: 12, color: "#9a3412", marginTop: 4 }}>
          Admin has been notified. Billing coming soon.
        </div>
      </div>
      <Button
        variant="ghost"
        data-testid="tier-upgrade-withdraw-btn"
        onClick={onWithdraw}
        disabled={withdrawing}
        style={{ color: "#9a3412" }}
      >
        {withdrawing ? "Withdrawing…" : "Withdraw request"}
      </Button>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Main page
 * ------------------------------------------------------------------ */
export default function PartnerTierUpgradePage() {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmReplacing, setConfirmReplacing] = useState(false);
  const [sending, setSending] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tiersRes, statusRes] = await Promise.all([
        api.get(_TIERS_ENDPOINT),
        api.get(_STATUS_ENDPOINT),
      ]);
      const list =
        tiersRes.data.tiers || tiersRes.data.items || tiersRes.data || [];
      // Sort ascending by subscription_price_cents.
      list.sort(
        (a, b) =>
          (a.subscription_price_cents || 0) - (b.subscription_price_cents || 0),
      );
      setTiers(list);
      setStatus(statusRes.data);
      // Redirect off this page if the user isn't fully-live yet.
      if (statusRes.data?.billing_status && !statusRes.data.current_active_tier) {
        navigate("/partner/onboarding", { replace: true });
      }
      setErrored(false);
    } catch (e) {
      setErrored(true);
      // eslint-disable-next-line no-console
      console.warn(
        "tier-upgrade fetch failed:",
        formatApiErrorDetail(e?.response?.data?.detail) || e.message,
      );
      if (e?.response?.status === 403) {
        navigate("/partner/onboarding", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRequestClick = ({ target, replacing }) => {
    setConfirmTarget(target);
    setConfirmReplacing(replacing);
  };

  const handleConfirm = async (note) => {
    if (!confirmTarget || sending) return;
    setSending(true);
    try {
      const res = await api.post(_REQUEST_ENDPOINT, {
        target_tier_id: confirmTarget.id,
        upgrade_note: note?.trim() || null,
      });
      toast.success(
        `Upgrade to ${res.data.target_tier.name} requested. Admin has been notified.`,
      );
      setConfirmTarget(null);
      await load();
    } catch (e) {
      const msg =
        formatApiErrorDetail(e?.response?.data?.detail) ||
        e?.response?.data?.detail?.message ||
        e?.message ||
        "Request failed. Try again.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const handleWithdraw = async () => {
    setWithdrawing(true);
    try {
      await api.post(_WITHDRAW_ENDPOINT);
      toast.success("Upgrade request withdrawn.");
      await load();
    } catch (e) {
      const msg =
        formatApiErrorDetail(e?.response?.data?.detail) ||
        e?.message ||
        "Couldn't withdraw. Try again.";
      toast.error(msg);
    } finally {
      setWithdrawing(false);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="tier-upgrade-loading"
        style={{ padding: 60, textAlign: "center" }}
      >
        <Loader2 className="animate-spin" size={24} />
      </div>
    );
  }
  if (errored) {
    return (
      <div
        data-testid="tier-upgrade-error"
        style={{
          margin: 40,
          padding: 24,
          border: "1px solid #fecaca",
          borderRadius: 12,
          background: "#fef2f2",
          color: "#b91c1c",
          fontSize: 14,
        }}
      >
        Couldn't load your tier info. Refresh the page or reach out to
        support.
      </div>
    );
  }

  const currentActiveId = status?.current_active_tier?.id;
  const pendingTargetId = status?.pending_intent_row?.target_tier_id;

  return (
    <div
      data-testid="tier-upgrade-page"
      style={{ padding: "32px 20px", maxWidth: 1100, margin: "0 auto" }}
    >
      <FloatingThemeToggle />
      <header style={{ marginBottom: 24 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#F89623",
            letterSpacing: 1.5,
            textTransform: "uppercase",
          }}
        >
          Your PooPartner tier
        </div>
        <h1
          data-testid="tier-upgrade-heading"
          style={{
            fontSize: 32,
            fontWeight: 800,
            color: "#374751",
            margin: "6px 0 0 0",
          }}
        >
          {status?.current_active_tier?.name || "Standard"}
        </h1>
        <p
          style={{
            fontSize: 13,
            color: "#6b7280",
            marginTop: 6,
          }}
        >
          Ready to grow? Request an upgrade any time — real billing
          is coming soon.
        </p>
      </header>

      <div
        style={{
          display: "grid",
          gap: 16,
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        }}
      >
        {tiers.map((tier) => (
          <TierCard
            key={tier.id}
            tier={tier}
            currentActiveId={currentActiveId}
            pendingTargetId={pendingTargetId}
            onRequestClick={handleRequestClick}
            disabled={sending}
          />
        ))}
      </div>

      <PendingIntentCard
        status={status}
        onWithdraw={handleWithdraw}
        withdrawing={withdrawing}
      />

      <ConfirmModal
        open={confirmTarget !== null}
        target={confirmTarget}
        replacing={confirmReplacing}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={handleConfirm}
        sending={sending}
      />
    </div>
  );
}
